import {
  type DateString,
  formatDate,
  parseDateString,
  toDateString,
  today,
} from '@seen/core/dates'
import { Button, type ButtonProps, cn, IconCalendar } from '@seen/ui'
import {
  type ElementRef,
  type PropsWithoutRef,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  MonthCalendar,
  type MonthCalendarProps,
} from '#src/components/calendar/MonthCalendar.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { PopoverTrigger } from '#src/components/core/popover/PopoverTrigger.tsx'
import { forwardRef } from '#src/utils/forwardRef.ts'

interface DatePickerBaseProps extends Omit<
  ButtonProps,
  | 'value'
  | 'onChange'
  | 'onBlur'
  | 'id'
  | 'aria-describedby'
  | 'aria-invalid'
  | 'disabled'
  | 'children'
> {
  value: DateString | null
  onChange: (value: DateString | null) => any
  onBlur?: () => void
  id?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  disabled?: boolean
  allowUndefined?: boolean
  placeholder?: string
  clearText?: ReactNode
  monthCalendarProps?: MonthCalendarProps
  hideIcon?: boolean
  // Native date inputs (e.g. birth date) put the calendar on the right.
  iconPosition?: 'start' | 'end'
  dateFormatter?: (date: DateString) => string
  /* Wraps the formatted value in the trigger. The picker keeps ownership of
   * formatting — this only decides how the finished string is rendered — so a
   * caller cannot end up displaying a differently-formatted date than the
   * surfaces around it. Used by the desk list view to mark a date that matched
   * the search. Omitted everywhere else, where the string renders as-is. */
  renderValue?: (formatted: string) => ReactNode
}

interface DatePickerAsChildProps extends DatePickerBaseProps {
  asChild: true
  children: ReactNode
}

interface DatePickerDefaultProps extends DatePickerBaseProps {
  asChild?: false
  children?: undefined
}

export type DatePickerProps = DatePickerAsChildProps | DatePickerDefaultProps

export const DatePicker = forwardRef<
  ElementRef<typeof Button>,
  PropsWithoutRef<DatePickerProps>
>((props, ref) => {
  const {
    value,
    onChange,
    onBlur,
    allowUndefined = false,
    placeholder = 'Date',
    clearText = 'Clear',
    className,
    monthCalendarProps,
    hideIcon = false,
    iconPosition = 'start',
    asChild = false,
    dateFormatter = formatDate,
    renderValue,
    ...delegated
  } = props
  const [open, setOpen] = useState(false)
  const [currentMonth, setCurrentMonth] = useState<Date | undefined>(
    value ? parseDateString(value) : undefined
  )

  const valueTodayOrNull = useMemo(() => {
    if (value === null && allowUndefined) {
      return null
    }
    return value ?? today()
  }, [value, allowUndefined])

  useEffect(() => {
    if (value) {
      setCurrentMonth(parseDateString(value))
    }
  }, [value])

  const formatted = value ? dateFormatter(value) : null
  const triggerContent =
    formatted === null
      ? placeholder
      : renderValue
        ? renderValue(formatted)
        : formatted

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (o && !value && !allowUndefined) {
          onChange(today())
        }
      }}
    >
      <PopoverTrigger onBlur={onBlur} asChild>
        {asChild ? (
          delegated.children
        ) : (
          <Button
            ref={ref}
            variant="outline"
            className={cn('h-auto gap-2 shadow-sm', className)}
            {...delegated}
          >
            {!hideIcon && iconPosition === 'start' && <IconCalendar />}
            {triggerContent}
            {!hideIcon && iconPosition === 'end' && <IconCalendar />}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="grid w-auto gap-3 p-3"
        align="start"
        onBlur={onBlur}
      >
        <Input
          type="date"
          variant="underline"
          className="justify-center"
          value={valueTodayOrNull ? toDateString(valueTodayOrNull) : ''}
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
        <MonthCalendar
          {...monthCalendarProps}
          mode="single"
          month={currentMonth}
          onMonthChange={(month) => {
            setCurrentMonth(month)
          }}
          selected={
            valueTodayOrNull ? parseDateString(valueTodayOrNull) : undefined
          }
          onSelect={(selected) => {
            if (selected) {
              onChange(toDateString(selected))
            }
            setOpen(false)
          }}
          className="p-0"
        />
        {clearText && (
          <Button
            variant="outline"
            onClick={() => {
              onChange(null)
              setOpen(false)
            }}
          >
            {clearText}
          </Button>
        )}
      </PopoverContent>
    </Popover>
  )
})
